import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  projectWorkflowService,
  type WorkflowStep,
  type WorkflowTemplate,
} from '@services/projectWorkflowService';
import { CACHE_DURATION } from '../utils/cache';

interface WorkflowTemplateState {
  templates: WorkflowTemplate[];
  defaultTemplateId: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: WorkflowTemplateState = {
  templates: [],
  defaultTemplateId: null,
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

const selectDefaultTemplate = (templates: WorkflowTemplate[]): WorkflowTemplate | null =>
  templates.find(template => template.isDefault) ?? templates[0] ?? null;

export const fetchWorkflowTemplates = createAsyncThunk<
  WorkflowTemplate[],
  { force?: boolean } | undefined,
  { state: { workflowTemplate: WorkflowTemplateState } }
>('workflowTemplate/fetchTemplates', async (options, { getState, rejectWithValue }) => {
  const force = options?.force ?? false;
  const state = getState().workflowTemplate;

  if (
    !force &&
    state.templates.length > 0 &&
    state.lastFetched &&
    Date.now() - state.lastFetched < CACHE_DURATION
  ) {
    return state.templates;
  }

  try {
    return await projectWorkflowService.getTemplates();
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to load workflow templates');
  }
});

export const saveDefaultWorkflowTemplate = createAsyncThunk<
  WorkflowTemplate,
  { templateId: string; steps: WorkflowStep[] }
>('workflowTemplate/saveDefaultTemplate', async ({ templateId, steps }, { rejectWithValue }) => {
  try {
    return await projectWorkflowService.updateGlobalTemplate(templateId, { steps });
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to save workflow template');
  }
});

const workflowTemplateSlice = createSlice({
  name: 'workflowTemplate',
  initialState,
  reducers: {
    setLocalDefaultTemplateSteps(state, action: PayloadAction<{ steps: WorkflowStep[] }>) {
      const { steps } = action.payload;
      const defaultTemplate = selectDefaultTemplate(state.templates);
      if (!defaultTemplate) return;

      state.templates = state.templates.map(template =>
        template.id === defaultTemplate.id
          ? {
              ...template,
              steps,
            }
          : template
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchWorkflowTemplates.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates = action.payload;
        const defaultTemplate = selectDefaultTemplate(action.payload);
        state.defaultTemplateId = defaultTemplate?.id ?? null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchWorkflowTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed';
      })
      .addCase(saveDefaultWorkflowTemplate.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveDefaultWorkflowTemplate.fulfilled, (state, action) => {
        state.isSaving = false;
        const savedTemplate = action.payload;
        let updatedExisting = false;

        state.templates = state.templates.map(template => {
          if (template.id !== savedTemplate.id) {
            return template;
          }
          updatedExisting = true;
          return {
            ...template,
            ...savedTemplate,
          };
        });

        if (!updatedExisting) {
          state.templates.push(savedTemplate);
        }

        const defaultTemplate = selectDefaultTemplate(state.templates);
        state.defaultTemplateId = defaultTemplate?.id ?? null;
        state.lastFetched = Date.now();
      })
      .addCase(saveDefaultWorkflowTemplate.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) || action.error.message || 'Failed';
      });
  },
});

export const { setLocalDefaultTemplateSteps } = workflowTemplateSlice.actions;
export default workflowTemplateSlice.reducer;
